import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fromScaled, toScaled } from '@loot/shared';
import {
  api,
  type CashAccount,
  type CashDivergence,
  type ReconBankLine,
  type ReconBookItem,
  type ReconOverview,
  type Reconciliation,
} from '../../api.ts';
import { FormModal, FormSection } from '../../components/FormModal.tsx';
import { ListToolbar } from '../../components/ListFilters.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  NumCell,
  Panel,
  Select,
  StatCard,
  Table,
  Textarea,
} from '../../components/ui.tsx';
import { CommentAction } from '../partners/common.tsx';
import { useFormat } from '../purchasing/common.tsx';
import { CardFilterNotice, useCardFilter } from './card-filter.tsx';

const KEY = ['finance', 'treasury', 'reconciliation'];
const DIFFERENCE_KINDS = ['fee', 'interest', 'fine', 'discount', 'fx', 'other'] as const;
const OPEN_FILTER = ['open_divergences'] as const;

const iso = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => iso(new Date(Date.now() - n * 86_400_000));
const bookKey = (b: { type: string; id: string }) => `${b.type}|${b.id}`;

/**
 * Conciliação bancária (ADR 0084, Entrega 9): casa as linhas do extrato com as baixas
 * registradas (pagamentos do Contas a Pagar e transferências). O sistema só sugere; quem confirma
 * é uma pessoa. Diferença exige tipo e motivo; desfazer é um registro novo, com motivo.
 */
export function TreasuryReconciliation() {
  const { t } = useTranslation();
  const [openOnly, clearOpen] = useCardFilter(OPEN_FILTER);
  const [accountKey, setAccountKey] = useState('');
  const [from, setFrom] = useState(() => daysAgo(30));
  const [to, setTo] = useState(() => iso(new Date()));
  const accounts = useQuery({
    queryKey: ['finance', 'treasury', 'accounts'],
    queryFn: () => api.get<{ accounts: CashAccount[] }>('/finance/treasury/accounts'),
  });
  if (accounts.isPending) return <Loading />;
  if (accounts.isError) return <ErrorBox error={accounts.error} />;
  const list = accounts.data.accounts;
  if (list.length === 0) {
    return (
      <Panel>
        <p className="text-sm text-muted">{t('finance.cash.noAccounts')}</p>
      </Panel>
    );
  }
  if (openOnly) {
    return (
      <OpenDivergences
        accounts={list}
        onClear={clearOpen}
        onOpenAccount={(key) => {
          setAccountKey(key);
          clearOpen();
        }}
      />
    );
  }
  const selected = list.find((a) => a.accountKey === accountKey) ?? list[0]!;
  return (
    <div className="flex flex-col gap-6">
      <ListToolbar>
        <Select
          id="recon-account"
          aria-label={t('finance.cash.fields.account')}
          value={selected.accountKey}
          onChange={(e) => setAccountKey(e.target.value)}
        >
          {list.map((a) => (
            <option key={a.accountKey} value={a.accountKey}>
              {`${a.legalEntityCode ?? '—'} · ${a.nickname} · ${a.masked} · ${a.currency}`}
            </option>
          ))}
        </Select>
        <Input
          id="recon-from"
          type="date"
          aria-label={t('finance.cash.fields.from')}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <Input
          id="recon-to"
          type="date"
          aria-label={t('finance.cash.fields.to')}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
      </ListToolbar>
      <AccountReconciliation accountKey={selected.accountKey} from={from} to={to} />
    </div>
  );
}

function useReconAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body }: { path: string; body: unknown }) =>
      api.post<Record<string, unknown>>(`/finance/treasury${path}`, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['finance', 'treasury'] });
    },
  });
}

function AccountReconciliation({
  accountKey,
  from,
  to,
}: {
  accountKey: string;
  from: string;
  to: string;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useReconAction();
  const [manual, setManual] = useState<ReconBankLine | null>(null);
  const data = useQuery({
    queryKey: [...KEY, accountKey, from, to],
    queryFn: () =>
      api.get<ReconOverview>(
        `/finance/treasury/accounts/${accountKey}/reconciliation?from=${from}&to=${to}`,
      ),
  });
  if (data.isPending) return <Loading />;
  if (data.isError) return <ErrorBox error={data.error} />;
  const o = data.data;
  const money = (v: string) => fmt.money(v, o.account.currency);
  const bookOf = new Map(o.bookItems.map((b) => [bookKey(b), b]));
  const confirm = (line: ReconBankLine, book: { type: string; id: string }) =>
    action.mutate({
      path: '/reconciliations',
      body: {
        accountKey,
        bankMovementIds: [line.id],
        bookItems: [book],
        method: 'suggested',
      },
    });
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StatCard
          label={t('finance.recon.bankWithoutBook')}
          value={o.discrepancies.bankWithoutBook}
          alert
        />
        <StatCard
          label={t('finance.recon.bookWithoutBank')}
          value={o.discrepancies.bookWithoutBank}
          alert
        />
      </div>
      <Divergences accountKey={accountKey} currency={o.account.currency} />
      <ErrorBox error={action.error} />
      <Panel title={t('finance.recon.openLinesTitle')}>
        <Table
          head={[
            t('finance.cash.fields.date'),
            t('finance.cash.fields.description'),
            { label: t('finance.cash.fields.amount'), align: 'right' },
            t('finance.recon.suggestion'),
            '',
          ]}
        >
          {o.bankLines.length === 0 && (
            <EmptyRow colSpan={5}>{t('finance.recon.noOpenLines')}</EmptyRow>
          )}
          {o.bankLines.map((line) => {
            const s = line.suggestion;
            const suggested = s?.status === 'suggested' ? bookOf.get(bookKey(s.book)) : undefined;
            return (
              <tr key={line.id}>
                <td className="whitespace-nowrap">{fmt.date(line.date)}</td>
                <td>
                  {line.description}
                  <div className="text-xs text-muted">
                    {t(`finance.cash.kinds.${line.kind}`)}{' '}
                    {line.situation !== 'pending' && (
                      <Badge tone={line.situation === 'in_review' ? 'info' : 'off'}>
                        {t(`finance.recon.situations.${line.situation}`)}
                      </Badge>
                    )}
                  </div>
                </td>
                <NumCell>{money(line.amount)}</NumCell>
                <td className="text-sm">
                  {suggested ? (
                    <>
                      <Badge tone="ok">{t('finance.recon.suggested')}</Badge> {suggested.label} ·{' '}
                      {fmt.date(suggested.date)}
                    </>
                  ) : s?.status === 'ambiguous' ? (
                    <Badge tone="accent">
                      {t('finance.recon.ambiguous', { count: s.candidates.length })}
                    </Badge>
                  ) : (
                    <span className="text-muted">{t('finance.recon.noSuggestion')}</span>
                  )}
                </td>
                <td className="text-right">
                  {o.canReconcile && (
                    <div className="flex justify-end gap-2">
                      {suggested && (
                        <Button
                          disabled={action.isPending}
                          onClick={() => confirm(line, suggested)}
                        >
                          {t('finance.recon.confirm')}
                        </Button>
                      )}
                      <Button variant="secondary" onClick={() => setManual(line)}>
                        {t('finance.recon.manual')}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </Table>
      </Panel>
      <Panel title={t('finance.recon.openBookTitle')}>
        <Table
          head={[
            t('finance.cash.fields.date'),
            t('finance.recon.item'),
            { label: t('finance.cash.fields.amount'), align: 'right' },
          ]}
        >
          {o.bookItems.length === 0 && (
            <EmptyRow colSpan={3}>{t('finance.recon.noOpenBook')}</EmptyRow>
          )}
          {o.bookItems.map((b) => (
            <tr key={bookKey(b)}>
              <td className="whitespace-nowrap">{fmt.date(b.date)}</td>
              <td>
                {b.label}
                <div className="text-xs text-muted">{t(`finance.recon.types.${b.type}`)}</div>
              </td>
              <NumCell>{money(b.amount)}</NumCell>
            </tr>
          ))}
        </Table>
      </Panel>
      <Reconciliations items={o.reconciliations} currency={o.account.currency} />
      {manual && (
        <ManualForm
          accountKey={accountKey}
          currency={o.account.currency}
          start={manual}
          bankLines={o.bankLines}
          bookItems={o.bookItems}
          onDone={() => setManual(null)}
        />
      )}
    </>
  );
}

function Reconciliations({ items, currency }: { items: Reconciliation[]; currency: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useReconAction();
  return (
    <Panel title={t('finance.recon.doneTitle')}>
      <ErrorBox error={action.error} />
      <Table
        head={[
          t('finance.recon.when'),
          t('finance.recon.lines'),
          { label: t('finance.recon.difference'), align: 'right' },
          t('finance.cash.fields.situation'),
          '',
        ]}
      >
        {items.length === 0 && <EmptyRow colSpan={5}>{t('finance.recon.noneDone')}</EmptyRow>}
        {items.map((r) => (
          <tr key={r.id}>
            <td className="text-xs">
              {fmt.dateTime(r.createdAt)}
              <div className="text-muted">
                {r.createdByName ?? '—'} · {t(`finance.recon.methods.${r.method}`)}
              </div>
            </td>
            <td className="text-sm">
              {t('finance.recon.linesSummary', {
                bank: r.lines.filter((l) => l.side === 'bank').length,
                book: r.lines.filter((l) => l.side === 'book').length,
              })}
              <div className="text-xs text-muted">
                {fmt.money(r.bankTotal, currency)} × {fmt.money(r.bookTotal, currency)}
              </div>
            </td>
            <NumCell>
              {r.difference === '0.0000' ? '—' : fmt.money(r.difference, currency)}
              {r.differenceKind && (
                <div className="text-xs text-muted">
                  {t(`finance.recon.kinds.${r.differenceKind}`)}
                </div>
              )}
            </NumCell>
            <td>
              <Badge tone={r.undone ? 'neutral' : r.situation === 'adjusted' ? 'accent' : 'ok'}>
                {r.undone
                  ? t('finance.recon.undone')
                  : t(`finance.recon.situations.${r.situation}`)}
              </Badge>
              {r.reason && <div className="text-xs text-muted">{r.reason}</div>}
              {r.undone && <div className="text-xs text-muted">{r.undone.reason}</div>}
            </td>
            <td className="text-right">
              <div className="flex justify-end gap-2">
                {r.canUndo && (
                  <CommentAction
                    label={t('finance.recon.undo')}
                    fieldLabel={t('finance.cash.fields.reason')}
                    variant="danger"
                    pending={action.isPending}
                    error={action.error}
                    onConfirm={(reason) =>
                      action.mutate({ path: `/reconciliations/${r.id}/undo`, body: { reason } })
                    }
                  />
                )}
                <MasterDeleteButton table="treasury.reconciliation" id={r.id} compact />
              </div>
            </td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

function ManualForm({
  accountKey,
  currency,
  start,
  bankLines,
  bookItems,
  onDone,
}: {
  accountKey: string;
  currency: string;
  start: ReconBankLine;
  bankLines: ReconBankLine[];
  bookItems: ReconBookItem[];
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useReconAction();
  const [bank, setBank] = useState<Set<string>>(() => new Set([start.id]));
  const [book, setBook] = useState<Set<string>>(() => new Set());
  const [kind, setKind] = useState('');
  const [reason, setReason] = useState('');
  const toggle = (set: Set<string>, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };
  const bankTotal = bankLines
    .filter((l) => bank.has(l.id))
    .reduce((s, l) => s + toScaled(l.amount), 0n);
  const bookTotal = bookItems
    .filter((b) => book.has(bookKey(b)))
    .reduce((s, b) => s + toScaled(b.amount), 0n);
  const difference = bankTotal - bookTotal;
  const money = (v: bigint) => fmt.money(fromScaled(v), currency);
  return (
    <FormModal
      open
      size="xl"
      title={t('finance.recon.manualTitle')}
      description={t('finance.recon.manualHint')}
      dirty
      saving={action.isPending}
      error={action.error}
      saveLabel={t('finance.recon.confirm')}
      onSave={() =>
        action.mutate(
          {
            path: '/reconciliations',
            body: {
              accountKey,
              bankMovementIds: [...bank],
              bookItems: bookItems
                .filter((b) => book.has(bookKey(b)))
                .map((b) => ({ type: b.type, id: b.id })),
              differenceKind: difference === 0n ? null : kind || null,
              reason: reason || null,
              method: 'manual',
            },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-sm font-medium">{t('finance.recon.bankSide')}</p>
          {bankLines.map((l) => (
            <label key={l.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={bank.has(l.id)}
                onChange={() => setBank((s) => toggle(s, l.id))}
              />
              {fmt.date(l.date)} · {l.description} · {fmt.money(l.amount, currency)}
            </label>
          ))}
        </div>
        <div>
          <p className="text-sm font-medium">{t('finance.recon.bookSide')}</p>
          {bookItems.length === 0 && (
            <p className="text-sm text-muted">{t('finance.recon.noOpenBook')}</p>
          )}
          {bookItems.map((b) => (
            <label key={bookKey(b)} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={book.has(bookKey(b))}
                onChange={() => setBook((s) => toggle(s, bookKey(b)))}
              />
              {fmt.date(b.date)} · {b.label} · {fmt.money(b.amount, currency)}
            </label>
          ))}
        </div>
        <p className="text-sm">
          {t('finance.recon.totals', {
            bank: money(bankTotal),
            book: money(bookTotal),
            difference: money(difference),
          })}
        </p>
        {difference !== 0n && (
          <FormSection>
            <Field label={t('finance.recon.differenceKind')} required>
              <Select id="recon-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="">{t('finance.recon.chooseKind')}</option>
                {DIFFERENCE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`finance.recon.kinds.${k}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('finance.cash.fields.reason')} className="sm:col-span-2" required>
              <Textarea
                id="recon-reason"
                required
                minLength={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
          </FormSection>
        )}
      </div>
    </FormModal>
  );
}

/** O que aconteceu em cada divergência, em uma frase. */
function useDivergenceDetail() {
  const { t } = useTranslation();
  const fmt = useFormat();
  return (d: CashDivergence) => {
    const money = (v: string) => fmt.money(v, d.currency);
    const x = d.details as Record<string, string | number | undefined>;
    switch (d.kind) {
      case 'balance_mismatch':
        return t('finance.recon.divergences.details.balance', {
          reported: money(String(x.reported)),
          calculated: money(String(x.calculated)),
        });
      case 'amount_mismatch':
        return t('finance.recon.divergences.details.amount', {
          label: x.label,
          bank: money(String(x.bankAmount)),
          book: money(String(x.bookAmount)),
        });
      case 'wrong_account':
        return t('finance.recon.divergences.details.account', {
          label: x.label,
          account: x.otherAccountNickname,
        });
      case 'currency_mismatch':
        return t('finance.recon.divergences.details.currency', {
          label: x.label,
          currency: x.currency,
        });
      case 'date_mismatch':
        return t('finance.recon.divergences.details.date', {
          expected: fmt.date(String(x.expectedDate)),
          effective: fmt.date(String(x.effectiveDate)),
        });
      case 'duplicate_receipt':
      case 'duplicate_payment':
        return t('finance.recon.divergences.details.duplicate', {
          description: x.description,
          count: x.count,
        });
      default:
        return String(x.description ?? x.label ?? '');
    }
  };
}

const divergenceTone = (st: CashDivergence['status']) =>
  st === 'pending' ? 'off' : st === 'in_review' ? 'info' : 'neutral';

/**
 * Divergências abertas de todas as contas, a lista do cartão da Visão geral. Reflete a última
 * conferência de cada conta; a análise continua na conciliação da conta.
 */
function OpenDivergences({
  accounts,
  onClear,
  onOpenAccount,
}: {
  accounts: CashAccount[];
  onClear: () => void;
  onOpenAccount: (accountKey: string) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const detail = useDivergenceDetail();
  const data = useQuery({
    queryKey: [...KEY, 'open-divergences'],
    queryFn: () => api.get<{ divergences: CashDivergence[] }>('/finance/treasury/divergences'),
  });
  const nameOf = new Map(
    accounts.map((a) => [a.accountKey, `${a.legalEntityCode ?? '—'} · ${a.nickname}`]),
  );
  return (
    <div className="flex flex-col gap-4">
      <CardFilterNotice label={t('finance.homeCards.openDivergences.title')} onClear={onClear} />
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {data.isSuccess && (
        <Table
          head={[
            t('finance.cash.fields.account'),
            t('finance.cash.fields.date'),
            t('finance.recon.divergences.kind'),
            { label: t('finance.cash.fields.amount'), align: 'right' },
            t('finance.cash.fields.situation'),
            '',
          ]}
        >
          {data.data.divergences.length === 0 && (
            <EmptyRow colSpan={6}>{t('finance.recon.divergences.none')}</EmptyRow>
          )}
          {data.data.divergences.map((d) => (
            <tr key={d.id}>
              <td className="text-xs">{nameOf.get(d.accountKey) ?? '—'}</td>
              <td className="whitespace-nowrap">{d.refDate ? fmt.date(d.refDate) : '—'}</td>
              <td>
                {t(`finance.recon.divergences.kinds.${d.kind}`)}
                <div className="text-xs text-muted">{detail(d)}</div>
              </td>
              <NumCell>{d.amount === null ? '—' : fmt.money(d.amount, d.currency)}</NumCell>
              <td>
                <Badge tone={divergenceTone(d.status)}>
                  {t(`finance.recon.divergences.statuses.${d.status}`)}
                </Badge>
                {d.note && <div className="text-xs text-muted">{d.note}</div>}
              </td>
              <td className="text-right">
                <Button variant="secondary" onClick={() => onOpenAccount(d.accountKey)}>
                  {t('finance.homeCards.openAccount')}
                </Button>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

/**
 * Divergências da conta (§15): a conferência registra o que precisa ser olhado; cada uma fica
 * pendente, em análise, resolvida ou descartada (com motivo). O que deixa de aparecer na
 * conferência é resolvido sozinho.
 */
function Divergences({ accountKey, currency }: { accountKey: string; currency: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useReconAction();
  const detail = useDivergenceDetail();
  const [all, setAll] = useState(false);
  const [scanned, setScanned] = useState<{ created: number; resolved: number } | null>(null);
  const data = useQuery({
    queryKey: [...KEY, accountKey, 'divergences', all],
    queryFn: () =>
      api.get<{ canManage: boolean; divergences: CashDivergence[] }>(
        `/finance/treasury/accounts/${accountKey}/divergences${all ? '?all=true' : ''}`,
      ),
  });
  const money = (v: string | null) => (v === null ? '—' : fmt.money(v, currency));
  return (
    <Panel title={t('finance.recon.divergences.title')}>
      <ListToolbar
        action={
          data.data?.canManage && (
            <Button
              disabled={action.isPending}
              onClick={() =>
                action.mutate(
                  { path: `/accounts/${accountKey}/divergences/scan`, body: {} },
                  {
                    onSuccess: (r) =>
                      setScanned({ created: Number(r.created), resolved: Number(r.resolved) }),
                  },
                )
              }
            >
              {t('finance.recon.divergences.scan')}
            </Button>
          )
        }
      >
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          {t('finance.recon.divergences.showClosed')}
        </label>
      </ListToolbar>
      {scanned && (
        <p className="text-sm text-muted">{t('finance.recon.divergences.scanned', scanned)}</p>
      )}
      <ErrorBox error={action.error} />
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {data.isSuccess && (
        <Table
          head={[
            t('finance.cash.fields.date'),
            t('finance.recon.divergences.kind'),
            { label: t('finance.cash.fields.amount'), align: 'right' },
            t('finance.cash.fields.situation'),
            '',
          ]}
        >
          {data.data.divergences.length === 0 && (
            <EmptyRow colSpan={5}>{t('finance.recon.divergences.none')}</EmptyRow>
          )}
          {data.data.divergences.map((d) => (
            <tr key={d.id}>
              <td className="whitespace-nowrap">{d.refDate ? fmt.date(d.refDate) : '—'}</td>
              <td>
                {t(`finance.recon.divergences.kinds.${d.kind}`)}
                <div className="text-xs text-muted">{detail(d)}</div>
              </td>
              <NumCell>{money(d.amount)}</NumCell>
              <td>
                <Badge tone={divergenceTone(d.status)}>
                  {t(`finance.recon.divergences.statuses.${d.status}`)}
                </Badge>
                {d.note && <div className="text-xs text-muted">{d.note}</div>}
                {d.resolution && (
                  <div className="text-xs text-muted">
                    {d.resolution}
                    {d.resolvedAutomatically
                      ? ` · ${t('finance.recon.divergences.automatic')}`
                      : d.closedByName && ` · ${d.closedByName}`}
                  </div>
                )}
              </td>
              <td className="text-right">
                {data.data.canManage && (d.status === 'pending' || d.status === 'in_review') && (
                  <div className="flex justify-end gap-2">
                    {d.status === 'pending' && (
                      <CommentAction
                        label={t('finance.recon.divergences.review')}
                        fieldLabel={t('finance.recon.divergences.note')}
                        pending={action.isPending}
                        error={action.error}
                        onConfirm={(note) =>
                          action.mutate({ path: `/divergences/${d.id}/review`, body: { note } })
                        }
                      />
                    )}
                    <CommentAction
                      label={t('finance.recon.divergences.resolve')}
                      fieldLabel={t('finance.cash.fields.reason')}
                      pending={action.isPending}
                      error={action.error}
                      onConfirm={(reason) =>
                        action.mutate({ path: `/divergences/${d.id}/resolve`, body: { reason } })
                      }
                    />
                    <CommentAction
                      label={t('finance.recon.divergences.dismiss')}
                      fieldLabel={t('finance.cash.fields.reason')}
                      variant="danger"
                      pending={action.isPending}
                      error={action.error}
                      onConfirm={(reason) =>
                        action.mutate({ path: `/divergences/${d.id}/dismiss`, body: { reason } })
                      }
                    />
                  </div>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  );
}
